/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Emitter from '@exc/emitter';
import {fetch} from '@adobe/exc-app/network';
import {getData} from '@exc/graphql';
import {
  getSession as getIMSSession,
  getOrgs,
  isSessionEnvValid,
  load,
  saveOrgsToLocalStorage,
  setSession
} from './utils/IMSUtils';
import logout from './utils/logout';
import metrics from '@adobe/exc-app/metrics';
import uuid from 'uuid/v1';

const defaultListeners = [
  ['imslib.accesstoken', 'auth.accesstoken'],
  ['imslib.accesstokenexpired', 'auth.accesstokenexpired'],
  ['imslib.profile', 'auth.profile'],
  ['imslib.error', 'auth.error'],
  ['imslib.ready', 'auth.ready']
];

/**
 * IMS Validate Token scheduler default interval in millis
 */
const QA_INTERVAL = 120000;
const PROD_INTERVAL = 30000;

class Auth extends Emitter {
  load(configuration) {
    const {env, imsConfig, listeners} = configuration;
    this.env = env;
    this.backupAccessTokensInfos = {};

    defaultListeners.forEach(lt => {
      document.addEventListener(lt[0], () => this.emit(lt[1]));
    });
    this.addListenersToAuth(listeners);
    this.logout = logout;
    this.logout.add(`https://exc-loginims.experience${env === 'prod' ? 'cloud' : '-qa.corp'}.adobe.com/exc-session/logout`);
    this.metrics = metrics.create('exc.auth.Auth');
    this.metrics.event('Auth.init');

    if (!window.adobeIMS || !window.adobeid) {
      return load(env, imsConfig).then(() => {
        this.isSessionEnvValid(imsConfig.environment, env);
        this.setupValidate(env === 'prod'  ? PROD_INTERVAL : QA_INTERVAL);
        return this;
      });
    }
    return this;
  }

  isAuthenticated(force) {
    return window.adobeIMS.isSignedInUser() || force;
  }

  signIn(referrer) {
    let oauthState = uuid();
    sessionStorage.setItem('oauthState', JSON.stringify(oauthState));
    this.logout.remove();
    window.adobeIMS.signIn({redirect_uri: referrer || '/', state: {oauthState}});
  }

  validateToken() {
    let {adobeIMS, adobeid} = window;
    const token = adobeIMS.getAccessToken();
    const clientId = adobeid.client_id;
    const params = {
      bearer_token: token,
      client_id: clientId,
      type: 'access_token'
    };
    const url = adobeIMS.IMSAPIs.validate_token;
    return new Promise((res, rej) => {
      adobeIMS.makeApiCall(url, params, response => {
        !response.valid ? rej() : res();
      });
    });
  }

  /** Validate session to prevent CSRF attack. */
  isValidSession() {
    let {adobeIMS} = window;
    const imsSessionState = adobeIMS.getState() && adobeIMS.getState().oauthState;
    const sessionStorageState = JSON.parse(sessionStorage.getItem('oauthState'));
    if (!(imsSessionState || sessionStorageState) || imsSessionState !== sessionStorageState) {
      return false;
    }
    return true;
  }

  /**
   * A scheduler to poll on ims validate token.
   * Generates: auth.validatetokensuccess event - on successful validation.
   * Generates: auth.validatetokenfailed event - on invalid/expired token.
   * @param {number} interval Polling interval in millis.
   */
  setupValidate(interval) {
    this.setIntervalId = setInterval(() => {
      this.validateToken()
        .then(() => this.emit('auth.validatetokensuccess'))
        .catch(() => {
          this.clear();
          this.emit('auth.validatetokenfailed');
        });
    }, interval);
    return this.setIntervalId;
  }

  async setCustomToken(token) {
    window.adobeid.standalone = {};
    await window.adobeIMS.setStandaloneToken(token);
    await pollForProfile();
  }

  /**
   * Async method to fetch the Current Org from IMS Session Data.
   * Returns the org Id of first ProdCtx if currentOrg isn't available in
   * session_data. To be used when IMS Session data has been updated with
   * current org info has been triggered during initial login&landing.
   * @returns {string} String with combination of current org id and auth src.
   */
  async fetchCurrentOrg() {
    const profile = this.fetchUserProfile();
    let sessionData = getData('imsSessionData', profile.session);
    if (!sessionData) {
      sessionData = await getIMSSession(profile.session, this.getClientId());
      this.metrics.event('failed to fetch session data from gql storage');
    }
    const localSession = JSON.parse(window.localStorage.getItem('unifiedShellSession'));

    const activeOrg = sessionData.current_active_org || (localSession && localSession.activeOrg);
    if (activeOrg) {
      // At times have received bad data from IMS session store
      // See https://jira.corp.adobe.com/browse/EXC-5288
      return activeOrg.org_id === 'AdobeOrg' ? null : `${activeOrg.org_id}@${activeOrg.org_auth_src}`;
    }
    return null;
  }

  fetchUserProfile() {
    const profile = window.adobeIMS.getUserProfile();
    if (!profile) {
      this.signOut(true);
    }
    return profile;
  }

  // add caching here separately if required
  async fetchProfileAndAccessTokenInfo(clientId, scope) {
    if (!clientId || !scope) {
      return null;
    }
    const masterClientId = this.getClientId();
    const IMSAPIs = window.adobeIMS.IMSAPIs;
    for (const key in IMSAPIs) {
      if ('endpoint' in IMSAPIs[key]) {
        IMSAPIs[key].endpoint[clientId] = IMSAPIs[key].endpoint[masterClientId];
      }
    }
    const params = {client_id: clientId, scope};
    return new Promise((resolve, reject) => {
      window.adobeIMS.makeApiCall(window.adobeIMS.IMSAPIs.check, params, response => {
        response ? resolve(response) : reject();
      });
    });
  }

  fetchAccessToken() {
    const accessTokenInfo = this.fetchAccessTokenInfo();
    return accessTokenInfo && accessTokenInfo.access_token;
  }

  fetchAccessTokenInfo() {
    return window.adobeIMS.getAccessTokenInfo();
  }

  async signOut(authExpired) {
    await this.logout.invoke();
    this.clear();
    const {origin, search} = window.location;

    // Restore the backed up access tokens info in the event that it was popped
    // and the log out redirect never happened.
    if (!Object.keys(window.adobeIMS._accessTokensInfos).length && this.backupAccessTokensInfos && Object.keys(this.backupAccessTokensInfos).length) {
      window.adobeIMS._accessTokensInfos = this.backupAccessTokensInfos;
    }
    // Back up the access token info just in case the user doesn't complete the
    // log out action (could happen if solution prevents default in the
    // `beforeunload` event).
    this.backupAccessTokensInfos = window.adobeIMS._accessTokensInfos;
    window.adobeIMS.signOut((search || authExpired) ? {} : {redirect_uri: `${origin}/login`});
  }

  clear() {
    ['orgsArr', 'shellCachedTokens', 'shellImsEnv', 'settingsSession', 'unifiedShellSession']
      .forEach(key => localStorage.removeItem(key));
    this.logout.remove();
    this.setIntervalId && clearInterval(this.setIntervalId);
  }

  /**
   * Get org id to org name map.
   * @returns {Object} Org name map.
   */
  async fetchOrgsMap() {
    const profile = this.fetchUserProfile();
    const productContext = profile.projectedProductContext;
    const pcEntries = productContext
      .map(pc => pc.prodCtx.owningEntity.split('@')[0]);

    try {
      const cachedOrgs = (JSON.parse(localStorage.getItem('orgsArr')) || {})[this.env];
      if (cachedOrgs && cachedOrgs.length === pcEntries.length &&
          cachedOrgs.every(org => pcEntries.indexOf(org.orgRef.ident) > -1)) {
        return cachedOrgs;
      }
    } catch (err) {
      this.metrics.error('Caught exception while parsing from orgsArr. Continuing.. ', err);
    }
    const orgsData = await getOrgs(this.getClientId(), this.env);
    this.metrics.event('failed to fetch org data from gql storage');
    return saveOrgsToLocalStorage(this.env, orgsData.filter(org => pcEntries.indexOf(org.orgRef.ident) > -1));
  }

  /**
   * Async method to handle org switching.
   * Each time an org switch occur, we need to call the Login Service to
   * authorize the new org and update IMS session data
   * and Renga last loggedIn tenant.
   * @param {string} newOrgId To be updated IMS Org Id.
   * @param {string} currentOrgId Current Org Id.
   * @returns {Object} Optimistically returns org id for update.
   */
  async changeOrg(newOrgId, currentOrgId) {
    const profile = this.fetchUserProfile();
    const accessToken = this.fetchAccessToken();
    const imsUserProfile = {
      access_token: accessToken,
      projectedProductContext: profile.projectedProductContext,
      session: profile.session,
      userId: profile.userId
    };
    const orgUpdateReqBody = {
      currentImsOrgId: currentOrgId,
      imsProfile: JSON.stringify(imsUserProfile),
      requestedImsOrgId: newOrgId
    };
    const url = `https://exc-loginims.experience${this.env === 'prod' ? 'cloud' : '-qa.corp'}.adobe.com/exc-session/imsorgupdate`;
    fetch(url, {
      body: JSON.stringify(orgUpdateReqBody),
      headers: {Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json'},
      method: 'POST'
    });
    let localSession = JSON.parse(window.localStorage.getItem('unifiedShellSession')) || {};

    // Store activeOrg in unifiedShellSession localStorage key, mimicking IMS session
    const [org_id, org_auth_src] = newOrgId.split('@');
    localSession.activeOrg = {org_auth_src, org_id};
    window.localStorage.setItem('unifiedShellSession', JSON.stringify(localSession));

    this.metrics.event('Auth.changeOrg.done', {currentOrgId, newOrgId});

    // Get localStorage activeOrg, return custom response
    return {activeOrgId: `${localSession.activeOrg.org_id}@${localSession.activeOrg.org_auth_src}`};
  }

  addListenersToAuth(listeners) {
    listeners &&
      Object.keys(listeners).forEach(event => {
        this.on(event, listeners[event]);
      });
  }

  removeListenersFromAuth(listeners) {
    listeners.length &&
      listeners.forEach(event => {
        this.all[event].forEach(fn => this.off(event, fn));
      });
  }

  async getSession() {
    const profile = this.fetchUserProfile();
    return getData('imsSessionData', profile.session) || await getIMSSession(profile.session, this.getClientId());
  }

  async setSession(sessionData) {
    const profile = this.fetchUserProfile();
    return await setSession(profile.session, sessionData, this.getClientId());
  }

  getClientId() {
    return window.adobeid.client_id;
  }

  isSessionEnvValid(imsEnv, buildEnv) {
    const isValid = isSessionEnvValid(imsEnv, buildEnv);
    if (!isValid) {
      this.metrics.event('Auth.ims.conflict');
      this.signOut(true);
    }
  }

  updateAndFetchUserProfile(isLogin) {
    if (!isLogin) {
      let gqlCachedUserProfile = getData('userProfileJson');
      if (!gqlCachedUserProfile) {
        // fetch Profile, in case GQL returns null in reload workflow
        return new Promise((resolve) => {
          window.adobeIMS.acquireAccessToken(() => resolve(this.fetchUserProfile()), (error) => {
            if (error && error.status === '401') {
              this.signOut();
            } else {
              this.metrics.error('gql and fetch profile call failed', error);
            }
          });
        });
      }
      // set fetched profile from gql in IMS Session storage
      window.adobeIMS.setProfile(gqlCachedUserProfile);
    }
    return Promise.resolve(this.fetchUserProfile());
  }
}
/**
   * Method to check and wait for profile every 100 ms before continuing.
   * @returns {Promise.<*>} Resolve when a profile exists.
   */
function pollForProfile() {
  return new Promise(res => {
    function check() {
      if (window.adobeIMS.getUserProfile()) {
        return res();
      }
      setTimeout(() => check(), 100);
    }
    check();
  });
}

let instance = new Auth();
let loading = null;

export const AuthInstance = async config => {
  if (!instance.ready) {
    if (!loading) {
      loading = instance.load(config);
    }

    return loading
      .catch(err => {
        throw err;
      })
      .then(() => {
        instance.ready = true;
        return instance;
      });
  }
  return instance;
};
